import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  ChevronLeft,
  ChevronRight,
  Delta,
  FloorRail,
  IconButton,
  Price,
  Segmented,
  TokenMark,
} from '@/components/ui';
import { useMarket } from '@/providers/market';
import { cn } from '@/lib/cn';
import { share, weiToNumber } from '@/lib/format';
import type { DemoToken } from '@/lib/demo/types';

export type SpotlightPreset = 'graduating' | 'floors' | 'new';

/** A preset needs at least this many tokens to be offered at all. */
const MIN_ITEMS = 3;
const MAX_ITEMS = 12;
/** Mini-card width + gap, so the arrows scroll exactly one card. */
const CARD_W = 260;
const STEP = CARD_W + 12;

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

const PRESET_META: Record<SpotlightPreset, { label: string; eyebrow: string; title: string }> = {
  graduating: { label: 'Graduating soon', eyebrow: 'Almost there', title: 'Graduating soon' },
  floors: { label: 'Deepest floors', eyebrow: 'Most protected', title: 'Deepest floors' },
  new: { label: 'Just launched', eyebrow: 'Fresh curves', title: 'Just launched' },
};

const PRESET_ORDER: SpotlightPreset[] = ['graduating', 'floors', 'new'];

function MiniCard({ token }: { token: DemoToken }) {
  const spot = weiToNumber(token.spotPrice);
  const ratio = clamp01(token.floorRatio);

  return (
    <li className="w-[260px] shrink-0">
      <Link
        to={`/token/${token.address}`}
        aria-label={`${token.symbol} — ${token.name}`}
        className="block rounded-xl"
      >
        <Card interactive padded={false} className="flex flex-col gap-2.5 p-3">
          <div className="flex items-center gap-2.5">
            <TokenMark symbol={token.symbol} hue={token.hue} glyph={token.glyph} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-xs font-semibold uppercase text-fg">
                {token.symbol}
              </div>
              <div className="truncate text-2xs text-muted">{token.name}</div>
            </div>
            <div className="shrink-0 text-right">
              <Price value={spot} className="block text-2xs text-fg" />
              <Delta value={token.change24h} className="text-2xs" />
            </div>
          </div>

          <div>
            <FloorRail floorRatio={ratio} height={4} />
            <div className="mt-1 flex items-center justify-between text-3xs text-subtle">
              <span className="uppercase tracking-[0.14em]">Floor</span>
              <span className="font-mono text-floor">{share(ratio)}</span>
            </div>
          </div>
        </Card>
      </Link>
    </li>
  );
}

/**
 * One rail, three lenses. Stacking three near-identical strips pushed the grid
 * below the fold, so the presets share a single scroller via a Segmented switch.
 */
export function SpotlightRail({ onSeeAll }: { onSeeAll: (preset: SpotlightPreset) => void }) {
  const { tokens } = useMarket();
  const [preset, setPreset] = useState<SpotlightPreset>('graduating');

  const scroller = useRef<HTMLUListElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const byPreset = useMemo<Record<SpotlightPreset, DemoToken[]>>(
    () => ({
      graduating: tokens
        .filter((t) => t.phase === 'graduating')
        .sort((a, b) => b.graduationProgress - a.graduationProgress)
        .slice(0, MAX_ITEMS),
      floors: [...tokens].sort((a, b) => b.floorRatio - a.floorRatio).slice(0, MAX_ITEMS),
      new: [...tokens].sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_ITEMS),
    }),
    [tokens],
  );

  // Only offer a lens that has enough tokens to look like a rail.
  const available = useMemo(
    () => PRESET_ORDER.filter((id) => byPreset[id].length >= MIN_ITEMS),
    [byPreset],
  );

  const active = available.includes(preset) ? preset : available[0];
  const items = active ? byPreset[active] : [];

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setOverflow(max > 4);
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft >= max - 4);
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, items.length, active]);

  // A lens switch resets the scroll position, so the first card is always visible.
  useEffect(() => {
    scroller.current?.scrollTo({ left: 0 });
  }, [active]);

  const nudge = (direction: -1 | 1) =>
    scroller.current?.scrollBy({ left: direction * STEP, behavior: 'smooth' });

  if (!active || available.length === 0) return null;

  const meta = PRESET_META[active];

  return (
    <section aria-labelledby="spotlight-title">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
            {meta.eyebrow}
          </p>
          <h2 id="spotlight-title" className="truncate font-display text-lg font-semibold text-fg">
            {meta.title}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          {available.length > 1 ? (
            <Segmented<SpotlightPreset>
              size="sm"
              items={available.map((id) => ({ value: id, label: PRESET_META[id].label }))}
              value={active}
              onChange={setPreset}
            />
          ) : null}

          <Button variant="ghost" size="xs" onClick={() => onSeeAll(active)}>
            See all
          </Button>

          {overflow ? (
            <div className="hidden items-center gap-1 sm:flex">
              <IconButton
                label={`Scroll ${meta.title} left`}
                size="sm"
                variant="outline"
                disabled={atStart}
                onClick={() => nudge(-1)}
              >
                <ChevronLeft size={14} />
              </IconButton>
              <IconButton
                label={`Scroll ${meta.title} right`}
                size="sm"
                variant="outline"
                disabled={atEnd}
                onClick={() => nudge(1)}
              >
                <ChevronRight size={14} />
              </IconButton>
            </div>
          ) : null}
        </div>
      </div>

      <ul
        ref={scroller}
        onScroll={measure}
        className={cn(
          'scroll-x no-scrollbar mt-3 flex list-none gap-3 pb-1',
          // The trailing fade only belongs there while there is more rail to reach.
          overflow && !atEnd && 'mask-fade-r',
        )}
        tabIndex={0}
        aria-label={`${meta.title}, horizontally scrollable`}
      >
        {items.map((token) => (
          <MiniCard key={token.address} token={token} />
        ))}
      </ul>
    </section>
  );
}
